import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { catchError, Observable, switchMap, throwError } from 'rxjs';

/**
 * `/v1/*` bearer injection + 401 refresh-and-retry (decisions/0009 D4, TASK-006).
 *
 * Bearer injection is scoped to `/v1/*` calls only — the demo Worker's own API surface
 * — never any external/third-party host (the legacy Strapi/Shopify/PayFast skip rules
 * are gone because those calls no longer carry a bearer-relevant token at all; the
 * scoping below achieves the same "don't leak our token to a third party" property
 * without needing per-host allow/deny lists, per the mapping doc §2 rewire target #4).
 *
 * @param req
 * @param next
 */
export const authInterceptor = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> =>
{
    const authService = inject(AuthService);
    const router = inject(Router);

    const isApiRequest = req.url.includes('/v1/');
    const isAuthRefreshOrLoginRequest = req.url.includes('/v1/auth/login') || req.url.includes('/v1/auth/refresh');

    let authReq = req;

    if ( isApiRequest && authService.accessToken && !AuthUtils.isTokenExpired(authService.accessToken) )
    {
        authReq = req.clone({
            headers: req.headers.set('Authorization', `Bearer ${authService.accessToken}`),
        });
    }

    // Response
    return next(authReq).pipe(
        catchError((error: unknown) =>
        {
            // Only `/v1/*` calls are ours to retry — and never retry a login/refresh
            // call itself (a failed login is a real credential failure, not a stale-
            // token one; retrying refresh-on-refresh-401 would loop).
            if ( !isApiRequest || isAuthRefreshOrLoginRequest || !(error instanceof HttpErrorResponse) || error.status !== 401 )
            {
                return throwError(() => error);
            }

            // Attempt exactly one silent refresh, then retry the original request with
            // the newly-issued token. If the refresh itself fails (session genuinely
            // over — see `AuthService.refresh()`'s own doc comment on this demo's
            // cross-origin refresh-cookie caveat), clear local state and send the user
            // back to sign-in rather than the legacy SweetAlert + full-page reload.
            return authService.refresh().pipe(
                switchMap(() =>
                {
                    const retriedReq = req.clone({
                        headers: req.headers.set('Authorization', `Bearer ${authService.accessToken}`),
                    });

                    return next(retriedReq);
                }),
                catchError(() =>
                {
                    authService.clearSession();
                    router.navigate(['/sign-in']);

                    return throwError(() => error);
                }),
            );
        }),
    );
};
